import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app, { db } from '../config/Firebase';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const inputHoverVariants = {
  hover: {
    borderColor: "#000000",
    boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.1)",
    transition: { 
      duration: 0.2,
      ease: "easeInOut"
    }
  }
};

const formHoverVariants = {
  hover: {
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-2px)",
    transition: { 
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const imagePreviewHover = {
  hover: {
    scale: 1.05,
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
    transition: { duration: 0.2 }
  }
};

const NewVisitor = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    gender: '',
    dob: '',
    contact: '',
    address: '',
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (e.g., JPG, PNG).');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB.');
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const storage = getStorage(app);

      let imageUrl = '';
      if (image) {
        const storageRef = ref(storage, `Visitor/${form.email}_${Date.now()}`);
        await uploadBytes(storageRef, image);
        imageUrl = await getDownloadURL(storageRef);
      }

      const visitorData = {
        ...form,
        imageUrl,
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(db, 'Visitors', form.email), visitorData);

      setSuccess('Visitor registered successfully!');
      setForm({
        fullName: '',
        email: '',
        gender: '',
        dob: '',
        contact: '',
        address: '',
      });
      setImage(null);
      setImagePreview(null);
      setTimeout(() => navigate('/Visitor'), 2000);
    } catch (err) {
      setError('Failed to register visitor. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="w-full max-w-2xl mx-auto mt-8 px-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className="bg-white p-8 rounded-lg border-2 border-black"
        variants={formHoverVariants}
        whileHover="hover"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center border-b-2 border-black pb-3 hover:text-gray-800 transition-colors">
          New Visitor Registration
        </h2>
        
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 text-red-700 p-4 rounded-md mb-6 border-l-4 border-red-500"
          >
            {error}
          </motion.div>
        )}
        
        {success && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 text-green-700 p-4 rounded-md mb-6 border-l-4 border-green-500"
          >
            {success}
          </motion.div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50"
              />
            </motion.div>
            
            <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
                Email ID
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
                Gender
              </label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50 cursor-pointer"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </motion.div>
            
            <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
              <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50 cursor-pointer"
              />
            </motion.div>
          </div>

          <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
              Contact Number
            </label>
            <input
              type="tel"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50"
            />
          </motion.div>

          <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
              Home Address
            </label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:border-black transition-all hover:bg-gray-50"
            />
          </motion.div>

          <motion.div variants={inputHoverVariants} whileHover="hover" className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2 hover:text-gray-900 transition-colors">
              Upload Image
            </label>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border-2 file:border-gray-300 file:rounded-md file:text-sm file:font-semibold file:bg-white file:text-gray-700 hover:file:border-black hover:file:bg-gray-50 transition-all cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-2 hover:text-gray-700 transition-colors">
                  JPG or PNG, max 5MB
                </p>
              </div>
              {imagePreview && (
                <motion.div 
                  className="relative"
                  variants={imagePreviewHover}
                  whileHover="hover"
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded-md border-2 border-gray-300 cursor-pointer"
                  />
                  <motion.button
                    type="button"
                    onClick={() => {
                      setImage(null);
                      setImagePreview(null);
                    }}
                    className="absolute -top-2 -right-2 bg-black text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-gray-800 transition-all"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ×
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.button
            type="submit"
            className="w-full bg-black text-white py-3 rounded-md font-semibold text-sm uppercase tracking-wider border-2 border-black hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 disabled:opacity-70 transition-all shadow-md hover:shadow-lg"
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
              transition: { duration: 0.3 }
            }}
            whileTap={{ 
              scale: 0.98,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
            }}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Register Visitor'
            )}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default NewVisitor;